import { useState, useEffect } from 'react';
import { Logger } from '../utils/Logger';
import { apiClient } from '../utils/ApiClient';

interface SystemModulesResponse {
    status: number;
    message: string;
    data: {
        modules: string[];
    };
}

export const useSystemModules = (enabled: boolean = true) => {
    const [modules, setModules] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    const fetchModules = async () => {
        if (!enabled) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            setError(null);

            Logger.debug('st-ck-client/src/hooks/useSystemModules.ts', 'Starting to fetch modules using ApiClient');
            Logger.debug('st-ck-client/src/hooks/useSystemModules.ts', `ApiClient base URL: ${(apiClient as any).baseUrl}`);
            
            // Use ApiClient instead of fetch (automatically includes Authorization header)
            const data = await apiClient.get('/system/modules');
            
            Logger.debug('st-ck-client/src/hooks/useSystemModules.ts', `API Response: ${JSON.stringify(data)}`);

            if (data.status === 200 && data.data?.modules) {
                setModules(data.data.modules);
                setRetryCount(0); // Reset retry count on success
            } else {
                throw new Error(data.message || 'Failed to fetch modules');
            }
        } catch (err: any) {
            const status = err?.status || err?.response?.status;
            const message = err?.message || err?.response?.data?.message;
            const errorMessage = message || (err instanceof Error ? err.message : 'Unknown error occurred');
            setError(errorMessage);
            Logger.error('st-ck-client/src/hooks/useSystemModules.ts', `Error fetching modules: ${err}`);
            Logger.error('st-ck-client/src/hooks/useSystemModules.ts', `Error message: ${errorMessage}`);
            
            // Stop retries on explicit authorization errors (403)
            if (status === 403 || /403|forbidden|access denied/i.test(String(errorMessage))) {
                // Do not retry
                Logger.debug('st-ck-client/src/hooks/useSystemModules.ts', 'Stopping retries due to 403/forbidden');
            } else if (retryCount < 3) {
                setTimeout(() => {
                    setRetryCount(prev => prev + 1);
                    fetchModules();
                }, 1000 * (retryCount + 1)); // Exponential backoff
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchModules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    // Reset error when modules are successfully fetched
    useEffect(() => {
        if (modules.length > 0 && error) {
            setError(null);
        }
    }, [modules, error]);

    return {
        modules,
        loading,
        error,
        refetch: fetchModules,
        retryCount
    };
}; 